import { useState, useMemo, useEffect } from 'react';
import { useGetEventContactsQuery, useDeleteContactsByContactIdsGuestListMutation, GetEventContactsDocument } from '@graphql/generated';
import { useToast, useDisclosure } from '@withjoy/joykit';
import { useTranslation, useHistory } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { RecipientSelectable, Contact } from '../../GuestList.types';

export const useRootController = (eventId: string, eventHandle: string, firebaseEventId: string) => {
  const { t } = useTranslation('guestList');
  const tAdmin = t('admin');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();
  const { toast } = useToast();
  const [recipients, setRecipients] = useState<RecipientSelectable[]>([]);

  const { data: contactsData, loading: isContactsQueryLoading } = useGetEventContactsQuery({
    variables: {
      firebaseEventId,
      eventId,
      forceGuestListSynch: true
    },
    batchMode: 'fast'
  });

  const [deleteContactsByContactIdsMutation, { loading: isDeleteContactsMutationLoading }] = useDeleteContactsByContactIdsGuestListMutation({
    // we need to refetch so this contact won't be included in the getEventContacts response
    refetchQueries: [
      {
        query: GetEventContactsDocument,
        variables: {
          firebaseEventId,
          eventId,
          forceGuestListSynch: true
        }
      }
    ],
    awaitRefetchQueries: true
  });

  const memoizedContacts = useMemo(() => {
    const contacts: RecipientSelectable[] = [];

    (contactsData?.getEventContacts as {
      firebasePartyKey: string;
      contacts: Contact[];
    }[])
      ?.filter(eventContact => !!eventContact)
      .forEach(eventContact => {
        const groupedContacts = eventContact.contacts;
        const groupedContactsLength = groupedContacts.length;
        if (!groupedContactsLength) {
          return;
        }
        // all the members that can not be identified in a household will be in the ungrouped group
        if (groupedContactsLength === 1 || eventContact.firebasePartyKey === 'ungrouped') {
          groupedContacts.forEach(contact => {
            contacts.push({
              id: contact.id,
              firstName: contact.firstName,
              lastName: contact.lastName,
              householdId: contact.household?.id ?? '',
              address: contact.household?.address
                ? {
                    address1: contact.household.address.address1,
                    address2: contact.household.address.address2,
                    city: contact.household.address.city,
                    country: contact.household.address.country,
                    countryCode: contact.household.address.countryCode,
                    postalCode: contact.household.address.postalCode,
                    region: contact.household.address.region
                  }
                : undefined,
              email: contact.email,
              phoneNumber: contact.phoneNumber?.[0]?.number,
              selected: false,
              fullName: `${contact.firstName} ${contact.lastName}`,
              envelopeNameOverride: contact.household.displayName,
              // no party indicator since there is the only member or it is a member of the ungrouped group
              partyIndicator: 'none',
              legacyAddressString: contact.legacyAddressString
            });
          });
        } else {
          // sort groupedContacts to always show first the groupLeader
          const arrayForSort = [...groupedContacts];
          arrayForSort
            .sort((a, b) => (b.GroupMember?.[0]?.groupLeader ? 1 : 0) - (a.GroupMember?.[0]?.groupLeader ? 1 : 0))
            .forEach((contact, index) => {
              contacts.push({
                id: contact.id,
                firstName: contact.firstName,
                lastName: contact.lastName,
                householdId: contact.household?.id ?? '',
                address: contact.household?.address
                  ? {
                      address1: contact.household.address.address1,
                      address2: contact.household.address.address2,
                      city: contact.household.address.city,
                      country: contact.household.address.country,
                      countryCode: contact.household.address.countryCode,
                      postalCode: contact.household.address.postalCode,
                      region: contact.household.address.region
                    }
                  : undefined,
                email: contact.email,
                phoneNumber: contact.phoneNumber?.[0]?.number,
                selected: false,
                fullName: `${contact.firstName} ${contact.lastName}`,
                envelopeNameOverride: contact.household.displayName,
                // the party indicator is assigned based on the groupLeader flag, then the rest of the group are in the middle, except the last member
                partyIndicator: contact.GroupMember?.[0]?.groupLeader ? 'head' : index === groupedContactsLength - 1 ? 'last' : 'middle',
                legacyAddressString: contact.legacyAddressString
              });
            });
        }
      });

    return contacts;
  }, [contactsData?.getEventContacts]);

  useEffect(() => setRecipients(memoizedContacts), [memoizedContacts]);

  const deleteRecipients = async (quantity: number) => {
    try {
      await deleteContactsByContactIdsMutation({ variables: { contactIds: recipients.filter(recipient => recipient.selected).map(recipient => recipient.id), firebaseEventId } });
      onClose();
      toast(`${quantity > 1 ? tAdmin.guests() : tAdmin.guest()} ${tAdmin.deleted()} ${tAdmin.successfully()}.`, { position: 'bottom-center' });
    } catch {
      //add telemetry
      toast(`${tAdmin.error()} ${tAdmin.delete()}.`, { position: 'bottom-center' });
    }
  };

  const createRecipient = useEventCallback(() => {
    history.push(`/${eventHandle}/edit/guestlist/create`);
  });

  const updateRecipient = useEventCallback((contactId: string) => {
    history.push(`/${eventHandle}/edit/guestlist/update/${contactId}`);
  });

  const selectedRecipientsQuantity = useMemo(() => recipients.filter(({ selected }) => selected).length, [recipients]);

  return {
    tAdmin,
    isLoading: isContactsQueryLoading,
    isDeleteContactsMutationLoading,
    recipients,
    setRecipients,
    createRecipient,
    updateRecipient,
    deleteRecipients,
    isOpen,
    onOpen,
    onClose,
    selectedRecipientsQuantity
  };
};
